import { useEffect } from "react";
import { useRouter } from "next/router";
import {
    useData,
    getDisplayedSlots,
    ROUTES,
    useEventDataOfSelectedEvent,
    EVENT_TYPE,
} from "@Utils";
import { useSetSlots } from "@Modules/eventPage/utils/useSetSlots";
import { IEvent } from "@Interfaces";
import { IDropdownSelector } from "@Modules/common/Form/FormInputs/DropdownSelector/IDropdownSelector";
import { getCartData } from "@Modules/eventPage/utils/cart";
import { ISelectedSlot, ISlotDropdownProps } from "./SlotDropdown";

const useSlotDropdown = ({ isMobile }: ISlotDropdownProps) => {
    const setSlots = useSetSlots();
    const router = useRouter();
    const { event, eventData } = useEventDataOfSelectedEvent();
    const {
        type,
        slots: eventSlots,
        metadata: {
            sessions_visible_on_booking_page: sessionsLimit,
            limit_sessions: isLimitSessionsEnabled,
        },
    } = eventData;

    const allowWebinarSessionAutoSelect =
        type === EVENT_TYPE.WEBINAR &&
        ((isLimitSessionsEnabled && sessionsLimit && sessionsLimit > 1) ||
            eventSlots.length > 1);

    const {
        paymentInfo: { slots },
    }: IEvent.IStore = event;

    const { data: ticketCount, setData: setTicketCount } = useData<number>(
        "ticketCount",
        1
    );
    const { data: selectedSlots, setData: setSelectedSlots } = useData<
        ISelectedSlot[]
    >("selectedSlots", []);

    const slotDisplay = getDisplayedSlots(eventData);

    const dateList = Array.from(new Set(slotDisplay.map(s => s.startDate)));

    const handleDateChange = (option: IDropdownSelector.Option | null) => {
        const newData: ISelectedSlot[] = Object.assign([], selectedSlots || []);
        setSlots([
            ...slots.filter(
                (s: string) => s !== newData?.[option?.index]?.uuid
            ),
        ]);

        newData[option?.index] = {
            date: option?.value ?? null,
            uuid: null,
        };
        setSelectedSlots(newData);
    };

    const handleSlotChange = (option: IDropdownSelector.Option | null) => {
        if (selectedSlots.length > option?.index) {
            const newData: ISelectedSlot[] = Object.assign(
                [],
                selectedSlots || []
            );
            setSlots([
                ...slots.filter(
                    (s: string) => s !== newData?.[option?.index]?.uuid
                ),
                option?.value || "",
            ]);
            newData[option?.index] = {
                date: newData[option?.index].date,
                uuid: option?.value || "",
            };
            setSelectedSlots(newData);
        }
    };

    const addSlot = () => {
        setSelectedSlots([
            ...(selectedSlots || []),
            {
                date: null,
                uuid: null,
            },
        ]);
        setTicketCount(ticketCount + 1 || 2); // ticketCount an be initialized as null at times, so this is to correct that
    };

    const removeSlot = (indexToRemove: number) => () => {
        if (ticketCount > 0) {
            setSlots([
                ...slots.filter(
                    (s: string) => s !== selectedSlots?.[indexToRemove]?.uuid
                ),
            ]);

            setSelectedSlots(
                (selectedSlots || []).filter(
                    (v: ISelectedSlot, index: number) => index !== indexToRemove
                )
            );
            setTicketCount(ticketCount - 1);
        }
    };

    const setDataBasedOnSlots = (slotsParam: string[] = slots) => {
        setTicketCount(Math.max(slots.length, 1));
        setSelectedSlots(
            slotsParam
                .map((slotId): ISelectedSlot | null => {
                    // Only pick the slot data for slots that are available for the current event
                    const selectionData = slotDisplay.filter(
                        slotData => slotData.uuid === slotId
                    );
                    if (selectionData.length > 0) {
                        return {
                            date: selectionData[0].startDate,
                            uuid: selectionData[0].uuid,
                        };
                    }
                    return null;
                })
                // Filter out the empty items for slot UUID's that are not a part of the current event
                .filter(s => s) as ISelectedSlot[]
        );
    };

    const resetData = () => {
        setSlots([]);
        setTicketCount(1);
        setSelectedSlots([]);
    };

    useEffect(() => {
        const slotList: string[] = slotDisplay.map(s => s.uuid);

        if (router.pathname !== ROUTES.CART.urlPath) {
            // If slots are already present in the store.event.paymentInfo.slots on the initial render of the event
            // Checks if any slot at all is not in the available event data

            if (slots.some(slot => !slotList.includes(slot))) {
                resetData();
            } else {
                // If the selected slots are all from the current event then do not delete them
                setDataBasedOnSlots();
            }
        } else {
            // if in cart page
            const cartData = getCartData();

            const cartEventData = cartData?.listingMap?.[eventData.uuid];
            const cartSlots = cartEventData?.slots || [];
            if (cartSlots.some(slot => !slotList.includes(slot))) {
                resetData();
            } else {
                setSlots(cartSlots);
                setDataBasedOnSlots(cartSlots);
            }
        }
    }, [eventData.uuid]);

    // autoFirstSelect if multiple session slots for live webinar only
    useEffect(() => {
        if (allowWebinarSessionAutoSelect && slotDisplay && slotDisplay[0]) {
            setSlots([slotDisplay[0].uuid]);
            setSelectedSlots([
                { date: slotDisplay[0].startDate, uuid: slotDisplay[0].uuid },
            ]);
        }
    }, []);

    return {
        selectedSlotsIndexs: Array.from(Array(ticketCount).keys()),
        addSlot,
        hideAddSlot:
            eventData?.metadata?.disable_multiple_slots ||
            eventData?.type === EVENT_TYPE.WEBINAR, // Hiding add slots explicitly for webinars
        eventData,
        isMobile,
        slotParams: {
            handleDateChange,
            handleSlotChange,
            selectedSlots,
            ticketCount,
            dateList,
            slots,
            slotDisplay,
            removeSlot,
            setSelectedSlots,
        },
    };
};

export default useSlotDropdown;
